import { computed } from '@vue/composition-api'
import datasetsConfig from '@/data/datasets.json'
import { getDatasetDefaultFrom, getDatasetModule } from '../config/datasets'
import moment from 'moment-timezone'
import { isRelativeTime, resolveTimeBound, toStreamlitRelative } from '../utils/timeUtils'
import { PROFILE_MAPPING } from '../config/sharedFilters'
import { excludeClosedStatuses, referencedHistories, restrictToLatestRows } from '../utils/currentStatus'

// Maps internal view identifiers to the API view value + default URL params
const VIEW_ALIASES = {
  dona: { view: 'ring', defaultParams: { innerRadius: 200 } },
  ring: { view: 'ring', defaultParams: { innerRadius: 0 } },
}

export function useChartUrl({
  height,
  baseUrl,
  selectedDataset,
  selectedView,
  theme,
  lang,
  token,
  activeFilters,
  live,
  refreshInterval,
  extraParams,
  viewParams,
  activeProcessFormLabel,
  processFormMode,
  processFormAnswer,
  processFormPairs,
  analysisAnswers,
  datasetRestriction,
  topRanked,
  currentStatus,
  openOnlyFilter,
}) {
  /**
   * Builds the full parameter set. `withTopRanked: false` yields the same chart
   * without the top-N restriction, which is what the ranking read is derived
   * from — otherwise the ranking would be measured over its own output and
   * freeze on the first result. `withCurrentStatus: false` does the same for
   * the current-status read.
   */
  const composeParams = ({ withTopRanked = true, withCurrentStatus = true } = {}) => {
    if (!selectedDataset.value || !selectedView.value) return null

    const params = new URLSearchParams()
    params.set('embed', 'true')
    params.set('dataset', selectedDataset.value)
    const viewAlias = VIEW_ALIASES[selectedView.value]
    const resolvedView = viewAlias ? viewAlias.view : selectedView.value
    params.set('view', resolvedView)
    if (viewAlias?.defaultParams) {
      Object.entries(viewAlias.defaultParams).forEach(([k, v]) => {
        params.set(k, String(v))
      })
    }
    params.set('theme', theme.value)
    if (lang.value) {
      params.set('lang', lang.value)
    }

    if (height.value && height.value > 0) {
      params.set('height', String(height.value))
    }

    if (token.value) {
      params.set('token', token.value)
    }

    // Append active filters — 'where' type filters are collected into a JSON object;
    // all others are set as individual URL params.
    const filters = activeFilters.value
    const module = getDatasetModule(selectedDataset.value)
    const whereClause = {}

    // ─── Analysis mode: override by + inject where for question answers ───
    const isAnalysisMode = processFormMode?.value === 'analysis' && activeProcessFormLabel?.value
    if (isAnalysisMode && module?.analysisConfig) {
      const { byField, dataColumn } = module.analysisConfig

      // Force by to the configured analysis field
      params.set('by', byField)

      // Exclude empty values from the by field
      whereClause[byField] = { operator: '!=', values: [''] }

      // Build like conditions from analysis answers
      // Épica 2: Support array of conditions per column for multi-question filtering
      const answers = analysisAnswers || {}
      const likeConditions = []
      Object.entries(answers).forEach(([question, answer]) => {
        if (answer) {
          likeConditions.push({
            operator: 'like',
            values: [`%"${question}": "${answer}"%`],
          })
        }
      })
      if (likeConditions.length > 0) {
        // Multiple conditions per column: use array format
        whereClause[dataColumn] = likeConditions.length === 1
          ? likeConditions[0]
          : likeConditions
      }
    }

    // ─── Count mode: inject processFormPairs as where clauses ───────────
    // When questions have answers selected, filter the data column using LIKE,
    // regardless of whether the selected field is a processForm field.
    // This ensures question-answer filters apply to the normal chart URL.
    if (!isAnalysisMode && activeProcessFormLabel?.value) {
      // Resolve the data column: analysisConfig → process-form filter column → fallback
      const dataColumn = module?.analysisConfig?.dataColumn
        || module?.filters?.find(f => f.filterType === 'process-form')?.optionsConfig?.column
        || `${selectedDataset.value}.data`

      const pairs = processFormPairs?.value || []
      const completedPairs = pairs.filter(p => p.question && p.answer)
      if (completedPairs.length > 0) {
        const likeConditions = completedPairs.map(p => ({
          operator: 'like',
          values: [`%"${p.question}": "${p.answer}"%`],
        }))
        // Merge with any existing conditions on the same column
        const existing = whereClause[dataColumn]
        if (existing) {
          const all = Array.isArray(existing) ? [...existing, ...likeConditions] : [existing, ...likeConditions]
          whereClause[dataColumn] = all
        } else {
          whereClause[dataColumn] = likeConditions.length === 1 ? likeConditions[0] : likeConditions
        }
      }
    }

    Object.keys(filters).forEach(filterKey => {
      const val = filters[filterKey]
      if (val !== null && val !== undefined && val !== '') {
        const moduleFilter = module?.filters?.find(f => f.key === filterKey)
        if (moduleFilter?.filterType === 'where' || moduleFilter?.filterType === 'where-date') {
          const column = moduleFilter.whereColumn || filterKey

          if (moduleFilter.filterType === 'where-date') {
            const rawVal = String(val)
            if (rawVal.includes(' to ')) {
              const [start, end] = rawVal.split(' to ')
              whereClause[column] = { operator: 'between', values: [`${start} 00:00:00`, `${end} 23:59:59`] }
            } else {
              whereClause[column] = { operator: 'like', values: [`${rawVal}%`] }
            }
          } else {
            const values = String(val).split(',').filter(Boolean)
            whereClause[column] = { operator: 'in', values }
          }
        } else if (moduleFilter?.filterType === 'process-form') {
          // Skip — handled separately for process-form URL
        } else {
          params.set(filterKey, String(val))
        }
      }
    })

    // Apply from/to defaults from datasets.json when not explicitly set
    const cfg = datasetsConfig[selectedDataset.value]
    const viewDefaults = (cfg && cfg.views && cfg.views[selectedView.value])
      ? cfg.views[selectedView.value].defaults || {}
      : {}
    if (!params.has('from')) {
      params.set('from', viewDefaults.from ? String(viewDefaults.from) : getDatasetDefaultFrom(selectedDataset.value))
    }
    if (!params.has('to')) {
      params.set('to', viewDefaults.to ? String(viewDefaults.to) : 'now')
    }

    // Épica 8: Apply module-level viewDefaults (e.g. limit: -1 for csv/json views)
    if (module?.viewDefaults?.[resolvedView]) {
      const vd = module.viewDefaults[resolvedView]
      Object.entries(vd).forEach(([k, v]) => {
        if (!params.has(k) && v !== '' && v !== null && v !== undefined) {
          params.set(k, String(v))
        }
      })
    }

    // Append view-specific params from the advanced panel
    if (viewParams && viewParams.value) {
      Object.entries(viewParams.value).forEach(([key, val]) => {
        if (val !== '' && val !== null && val !== undefined) {
          // Serialize arrays/objects as JSON strings
          if (Array.isArray(val) || (typeof val === 'object' && val !== null)) {
            params.set(key, JSON.stringify(val))
          } else {
            params.set(key, String(val))
          }
        }
      })
    }

    // Append optional advanced params (explicit props override view panel params)
    if (extraParams && extraParams.value) {
      Object.entries(extraParams.value).forEach(([key, val]) => {
        if (val !== '' && val !== null && val !== undefined) {
          // In analysis mode, don't let extraParams override 'by' — we force it
          if (isAnalysisMode && key === 'by') return
          params.set(key, val)
        }
      })
    }

    // Apply field-level defaultParams for every column referenced by 'by'.
    // Splits comma-separated multi-field selections (e.g. timeseries Campo 1 + Campo 2)
    // so each field can contribute its own defaults (freq from a date field,
    // columns=$sum from a cost field, etc.). Existing params win.
    const activeBy = params.get('by')
    if (activeBy && module?.fields) {
      String(activeBy).split(',').map(k => k.trim()).filter(Boolean).forEach(byKey => {
        const matchedField = module.fields.find(f => f.key === byKey)
        if (!matchedField?.defaultParams) return
        Object.entries(matchedField.defaultParams).forEach(([key, val]) => {
          if (!params.has(key) && val !== '' && val !== null && val !== undefined) {
            params.set(key, String(val))
          }
        })
      })
    }

    // Épica 12: Automatically apply PROFILE_MAPPING if users.id_profile is used
    const activeColumns = params.get('columns') || ''
    const activeWhere = params.get('where') || ''
    const involvesProfile = activeBy?.includes('users.id_profile') || 
                           activeColumns.includes('users.id_profile') || 
                           activeWhere.includes('users.id_profile') ||
                           activeBy?.includes('id_profile') ||
                           activeColumns.includes('id_profile') ||
                           activeWhere.includes('id_profile')

    if (involvesProfile) {
      const existingMappingStr = params.get('mapping')
      let mergedMapping = { 
        'users.id_profile': PROFILE_MAPPING,
        'id_profile': PROFILE_MAPPING 
      }

      if (existingMappingStr) {
        try {
          const parsed = JSON.parse(existingMappingStr)
          mergedMapping = { ...parsed, ...mergedMapping }
        } catch (e) { /* ignore */ }
      }
      params.set('mapping', JSON.stringify(mergedMapping))
    }

    // A dataset whose time filter is unusable declares omitTimeRange (see the
    // hanwha_cameras module): its URL travels with no window at all. Dropped
    // after every source has run, so neither a saved config nor an explicit
    // prop can slip one back in — and no window means no live refresh.
    if (module?.omitTimeRange) {
      params.delete('from')
      params.delete('to')
    }

    // Live / Refresh logic: default behaviour is to refresh when using
    // relative time ranges (now-*). Setting live=false disables it.
    const hasRelativeTime = (() => {
      const fromVal = params.get('from') || ''
      const toVal = params.get('to') || ''
      return fromVal.includes('now') || toVal.includes('now')
    })()

    if (hasRelativeTime && live.value !== false) {
      params.set('REFRESH_INTERVAL', String(refreshInterval.value))
      params.set('live', 'true')
    } else if (live.value === false) {
      params.set('live', 'false')
    }

    // Streamlit resolves `now` on a UTC clock against local-time columns, so a
    // raw `now-10m` never matches. A live chart gets the window shifted into
    // the server clock (toStreamlitRelative) and keeps rolling on every
    // refresh; a static one gets fixed timestamps. Bare dates and times without
    // seconds are widened to the whole day / minute the user picked.
    const keepRolling = hasRelativeTime && live.value !== false
    const anchor = moment()
    const fromParam = params.get('from')
    if (fromParam) {
      params.set('from', keepRolling && isRelativeTime(fromParam)
        ? toStreamlitRelative(fromParam, anchor)
        : resolveTimeBound(fromParam))
    }
    const toParam = params.get('to')
    if (toParam) {
      params.set('to', keepRolling && isRelativeTime(toParam)
        ? toStreamlitRelative(toParam, anchor)
        : resolveTimeBound(toParam, { end: true }))
    }

    // Permission-restricted access (reached via a permissionFallback) injects
    // a forced 'where' clause that overrides everything else for its columns.
    const forcedWhere = datasetRestriction?.value?.forcedWhere || null

    // Merge whereClause filters with any existing 'where' param (e.g. from extraParams prop).
    // Profile-level 'where' filters take lower precedence than explicitly passed 'where' prop.
    if (Object.keys(whereClause).length > 0 || forcedWhere) {
      const existingWhere = params.get('where')
      let merged = whereClause
      if (existingWhere) {
        try {
          const parsed = JSON.parse(existingWhere)
          merged = { ...whereClause, ...parsed }
        } catch (e) { /* keep merged = whereClause */ }
      }
      // Forced where wins over user filters and the explicit 'where' prop.
      if (forcedWhere) merged = { ...merged, ...forcedWhere }
      params.set('where', JSON.stringify(merged))
    }

    // «Solo abiertos»: leave out the records whose current status is closed.
    // Also in the unrestricted URL, so a chart that does not otherwise read a
    // history status gets the read below and one row per record (audits by
    // REMS: 64 → 28 on Banamex staging, 223 without the restriction).
    excludeClosedStatuses(params, openOnlyFilter?.value)

    // Status kept in a history table (see useCurrentStatus): when the chart
    // reads that history, only the latest row of each record may join, so a
    // record counts once with its current status. Measured on
    // security_self_audits: one row per answer again (265 → 62 on Banamex
    // staging); answers with no status row at all drop out while it applies.
    if (withCurrentStatus) {
      const status = currentStatus?.value
      if (status?.config && status.latest) {
        restrictToLatestRows(params, referencedHistories(params, selectedDataset.value, status.config), status.latest)
      }
    }

    // Top-N: restrict the chart to the ranked winners through the dataset's own
    // filter param. Applied last so it wins over a manual filter on the same
    // column — the ranking was computed with that filter already active, so the
    // winners are a subset of it and overwriting is the intersection.
    // A `where` clause is deliberately NOT used: on operational_security its
    // mere presence changes the grain of `$count` (23577 without it versus 1033
    // with an always-true condition), so it would rewrite the very numbers it
    // is meant to filter. See §12.E of charts_embedder_documentation.md.
    if (withTopRanked) {
      const ranking = topRanked?.value
      if (ranking?.enabled && ranking.param && ranking.value) {
        params.set(ranking.param, ranking.value)
      }
    }

    return params
  }

  const buildUrl = (params) => {
    if (!params) return ''

    const base = baseUrl.value.endsWith('/')
      ? baseUrl.value.slice(0, -1)
      : baseUrl.value

    // Decode commas so Streamlit receives literal commas (e.g. by=a,b)
    return base + '/?' + params.toString().replace(/%2C/gi, ',')
  }

  const iframeUrl = computed(() => buildUrl(composeParams()))

  /** The same chart minus the top-N restriction: the ranking's own source. */
  const chartUrlWithoutTopRanked = computed(() => buildUrl(composeParams({ withTopRanked: false })))

  /** The chart with neither restriction: the current-status read's own source. */
  const chartUrlUnrestricted = computed(() => buildUrl(composeParams({ withTopRanked: false, withCurrentStatus: false })))

  // ─── Process-form payload ──────────────────────────────
  // When 'by' matches a field with processForm: true, expose the
  // payload for the external POST call (handled by Index.vue).
  // Only active in 'count' mode (not 'analysis').
  // Épica 10: Re-enabled with array-based where conditions
  const processFormConfig = computed(() => {
    if (!selectedDataset.value || !selectedView.value) return null
    if (processFormMode?.value === 'analysis') return null

    const pfLabel = activeProcessFormLabel?.value
    if (!pfLabel) return null

    const module = getDatasetModule(selectedDataset.value)
    if (!module?.fields) return null

    const matchedField = module.fields.find(f => f.processForm)
    if (!matchedField) return null

    // Collect completed pairs (both question and answer set)
    const pairs = processFormPairs?.value || []
    const completedPairs = pairs.filter(p => p.question && p.answer)
    if (completedPairs.length === 0) return null

    if (!iframeUrl.value) return null

    // Build CSV URL cleanly (same pattern as defaultProcessFormUrl),
    // adding the where filter for all completed question+answer pairs.
    // Same column as the count-mode filter above: `<dataset>.data` only exists
    // where the dataset is named after its answers table, and a `where` on a
    // missing column answers HTTP 500 (measured 2026-10-26 on
    // security_self_audits, simulacrums and operational_procedures).
    const dataColumn = module.analysisConfig?.dataColumn
      || module.filters?.find(f => f.filterType === 'process-form')?.optionsConfig?.column
      || `${selectedDataset.value}.data`
    const dataKey = dataColumn.split('.').pop()

    // The csv view returns identical rows once, so answers with the same JSON
    // would count as one (measured 2026-10-26: 64 self-audit answers → 39
    // rows). The answer's own id keeps them apart; the API finds `data` by its
    // header.
    const answersTable = dataColumn.includes('.') ? dataColumn.split('.')[0] : ''

    const whereConditions = completedPairs.map(p => ({
      operator: 'like',
      values: [`%"${p.question}": "${p.answer}"%`],
    }))

    const whereCondition = { [dataColumn]: whereConditions }

    const streamlitBase = baseUrl.value.replace(/\/+$/, '')

    // Extract from/to from iframeUrl
    const iframeQuery = iframeUrl.value.split('/?')[1]
    const iframeParams = new URLSearchParams(iframeQuery || '')

    const csvParams = new URLSearchParams()
    csvParams.set('dataset', selectedDataset.value)
    csvParams.set('limit', '-1')
    csvParams.set('columns', answersTable ? `${answersTable}.id,${dataKey}` : dataKey)
    csvParams.set('view', 'csv')
    if (!module.omitTimeRange) {
      csvParams.set('from', iframeParams.get('from') || getDatasetDefaultFrom(selectedDataset.value))
      if (iframeParams.has('to')) csvParams.set('to', iframeParams.get('to'))
    }
    if (token.value) csvParams.set('token', token.value)

    // Merge processForm where with any existing where from filters
    const existingWhere = iframeParams.get('where')
    if (existingWhere) {
      try {
        const parsed = JSON.parse(existingWhere)
        csvParams.set('where', JSON.stringify({ ...parsed, ...whereCondition }))
      } catch (e) {
        csvParams.set('where', JSON.stringify(whereCondition))
      }
    } else {
      csvParams.set('where', JSON.stringify(whereCondition))
    }

    const csvUrl = `${streamlitBase}/?${csvParams.toString().replace(/%2C/gi, ',')}`

    // Use first completed question as the API grouping key
    return { by: completedPairs[0].question, view: selectedView.value, url: csvUrl }
  })

  return { iframeUrl, processFormConfig, chartUrlWithoutTopRanked, chartUrlUnrestricted }
}
